import React from "react";
import { Area, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent } from "../ui/card";
import { FactorStrip } from "./Factors";
import { Mini, Overline, StockHeader, ThesisStrip } from "./Shared";
import { DELL_DAYS, DELL_PATH, STOCK, baseRates as B, fmtPct, pathAt } from "./data";

const TT = { background: "#18181b", border: "1px solid #27272a", fontSize: 11 };

const ClockRing = ({ day }) => {
  const f = day / 126;
  return (
    <div className="relative w-44 h-44 shrink-0" data-testid="kfm-trade-clock-ring">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="44" fill="none" stroke="#27272a" strokeWidth="6" />
        <circle cx="50" cy="50" r="44" fill="none" stroke="#38bdf8" strokeWidth="6" strokeLinecap="round" strokeDasharray={`${f * 276.5} 276.5`} />
        {[21, 63, 126].map((m) => {
          const a = (m / 126) * 2 * Math.PI;
          return <circle key={m} cx={50 + 44 * Math.cos(a)} cy={50 + 44 * Math.sin(a)} r="1.6" fill="#a1a1aa" />;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">day</span>
        <span className="text-4xl font-black font-mono" data-testid="kfm-trade-clock-day-counter">{day}</span>
        <span className="text-[10px] font-mono text-muted-foreground">of 126 · {Math.round(f * 100)}%</span>
      </div>
    </div>
  );
};

const PathChart = () => {
  const data = B.path_s8.map((r) => {
    const k = DELL_DAYS.indexOf(r.day);
    return { day: r.day, band: [r.p25, r.p75], p50: r.p50, dell: k >= 0 ? DELL_PATH[k] : undefined };
  });
  if (!data.some((d) => d.day === STOCK.day)) {
    const t = pathAt(STOCK.day);
    data.push({ day: STOCK.day, band: [t.p25, t.p75], p50: t.p50, dell: STOCK.sinceDisclosure });
  }
  data.sort((a, b) => a.day - b.day);
  return (
    <div className="h-64" data-testid="kfm-trade-clock-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 12, bottom: 4, left: -14 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="#27272a" vertical={false} />
          <XAxis dataKey="day" type="number" domain={[0, 126]} ticks={[0, 21, 42, 63, 84, 105, 126]} tick={{ fontSize: 10, fill: "#71717a" }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 10, fill: "#71717a" }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
          <Tooltip contentStyle={TT} formatter={(v, k) => [Array.isArray(v) ? `${v[0].toFixed(1)}% … ${v[1].toFixed(1)}%` : `${Number(v).toFixed(1)}%`, k === "band" ? "S8 middle half" : k === "p50" ? "S8 typical" : "DELL"]} labelFormatter={(l) => `day ${l}`} />
          <ReferenceLine y={0} stroke="#52525b" />
          <ReferenceLine x={STOCK.day} stroke="#38bdf8" strokeDasharray="3 3" label={{ value: "today", position: "top", fontSize: 10, fill: "#38bdf8" }} />
          <Area dataKey="band" stroke="none" fill="#a1a1aa" fillOpacity={0.12} isAnimationActive={false} />
          <Line dataKey="p50" stroke="#a1a1aa" strokeDasharray="4 3" dot={false} isAnimationActive={false} />
          <Line dataKey="dell" stroke="#38bdf8" strokeWidth={2.5} dot={false} connectNulls isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default function VariantClock() {
  const t = pathAt(STOCK.day);
  const pct = STOCK.sinceDisclosure >= t.p75 ? "top quartile" : STOCK.sinceDisclosure >= t.p50 ? "above the typical path" : STOCK.sinceDisclosure >= 0 ? "below the typical path" : "negative";
  return (
    <div className="space-y-4" data-testid="kfm-variant-b">
      <StockHeader compact />
      <Card data-testid="kfm-variant-b-hero"><CardContent className="pt-5 pb-5">
        <Overline className="mb-3">Trade clock — alpha since the 13F disclosure vs cohorts in the same band</Overline>
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex flex-col items-center gap-4 lg:w-64">
            <ClockRing day={STOCK.day} />
            <span className="px-2 py-1 rounded border text-[10px] font-mono whitespace-nowrap bg-emerald-500/15 text-emerald-400 border-emerald-500/40" data-testid="kfm-trade-clock-status-badge">
              CONFIRMING · {pct}
            </span>
            <div className="grid grid-cols-2 gap-3 w-full">
              <Mini k="Delivered so far" v={`${fmtPct(STOCK.sinceDisclosure)}`} accent="text-sky-300" />
              <Mini k="S8 typical today" v={fmtPct(t.p50)} />
              <Mini k="S8 middle half" v={`${fmtPct(t.p25)} … ${fmtPct(t.p75)}`} />
              <Mini k="Window left" v={`${126 - STOCK.day} sessions`} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <PathChart />
            <div className="text-[11px] text-muted-foreground mt-1">
              blue = DELL since disclosure ({STOCK.revealDate}) · grey band = middle half of all {B.path_n.toLocaleString()} S8 cohorts (broader than the Odds profile) · dashed = typical (median) path · sector-relative
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              <div className="rounded border border-border/60 p-3"><Overline>Matched price pattern</Overline><div className="font-mono text-sm mt-1">{STOCK.regime}</div><div className="text-[11px] text-muted-foreground">nowcast archetype · p 0.61 · strong family</div></div>
              <div className="rounded border border-border/60 p-3"><Overline>21-session trend</Overline><div className="font-mono text-sm mt-1 text-emerald-400">{fmtPct(STOCK.trend21)} vs sector</div><div className="text-[11px] text-muted-foreground">the one validated mid-holding check</div></div>
              <div className="rounded border border-border/60 p-3"><Overline>How to read</Overline><div className="text-[11px] mt-1">Ahead ≠ take profit; behind ≠ add more — both were tested and rejected. Only a fading trend changes the view.</div></div>
            </div>
          </div>
        </div>
      </CardContent></Card>
      <ThesisStrip />
      <FactorStrip prefix="kfm-b" />
    </div>
  );
}
